/**
 * SQL building blocks for deriving IAM role values from `iam_roles`/
 * `iam_actor_roles` instead of the deprecated `user_companies.role` column.
 * Shared across the users list/search queries that filter or sort on this
 * column live in SQL, via a per-row correlated `LATERAL` join rather than N+1
 * `PermissionService` calls.
 */

const roleKeySeniorityCase = (keyExpr: string): string => `
  CASE ${keyExpr}
    WHEN 'retraced-admin' THEN 0
    WHEN 'retraced-sales' THEN 1
    WHEN 'retraced-product-development' THEN 2
    WHEN 'retraced-customer-success' THEN 3
    WHEN 'retraced-marketing' THEN 4
    WHEN 'company-admin' THEN 5
    ELSE 6
  END
`;

/**
 * Common `LEFT JOIN LATERAL` scaffolding shared by every role-resolving join
 * below: correlate on `actorIdExpr`, filter by `tenantFilterExpr`, and pick
 * one row via `orderByExpr` (most senior/highest-priority first).
 */
const buildRoleLateralJoinSkeleton = (
  actorIdExpr: string,
  tenantFilterExpr: string,
  orderByExpr: string,
  alias: string,
): string => `
  LEFT JOIN LATERAL (
    SELECT ir.key AS role_key, ir.name AS role_name
    FROM iam_actor_roles iar
    JOIN iam_roles ir ON ir.id = iar.role_id
    WHERE iar.actor_type = 'user' AND iar.actor_id = ${actorIdExpr}
      AND ${tenantFilterExpr}
    ORDER BY ${orderByExpr}
    LIMIT 1
  ) ${alias} ON true
`;

/**
 * `LATERAL` join resolving the most senior IAM role `actorIdExpr` holds in
 * the tenant `tenantIdExpr`, aliased as `alias`. Replaces
 * `user_companies.role` (or an equivalent single-tenant role column).
 *
 * `extraFilterExpr` narrows which rows resolve a role at all — the
 * company-perspective lists pass the caller's own company, so a row belonging
 * to somebody else's tenant comes back with no role rather than that tenant's.
 * A role key is not a neutral label: a custom set passes through this
 * expression unchanged, so an unrestricted join would name another company's
 * permission model to a caller who cannot read it.
 */
export const buildRoleLateralJoin = (
  actorIdExpr: string,
  tenantIdExpr: string,
  alias: string,
  extraFilterExpr?: string,
): string =>
  buildRoleLateralJoinSkeleton(
    actorIdExpr,
    extraFilterExpr
      ? `ir.tenant_id = ${tenantIdExpr} AND ${extraFilterExpr}`
      : `ir.tenant_id = ${tenantIdExpr}`,
    `(${roleKeySeniorityCase('ir.key')})`,
    alias,
  );

/**
 * Scalar subquery yielding the display names of EVERY IAM role `actorIdExpr`
 * holds in the tenant `tenantIdExpr`, most senior first (ties by name so the
 * order is stable). Unlike `buildRoleLateralJoin` it is not reduced to one
 * role, and an actor with none gets `{}` (an empty array), never `NULL` —
 * `ARRAY(subquery)` has no NULL case. Selected directly, no join needed.
 */
export const buildRoleNamesArraySubquery = (
  actorIdExpr: string,
  tenantIdExpr: string,
): string => `
  ARRAY(
    SELECT ir.name
    FROM iam_actor_roles iar
    JOIN iam_roles ir ON ir.id = iar.role_id
    WHERE iar.actor_type = 'user' AND iar.actor_id = ${actorIdExpr}
      AND ir.tenant_id = ${tenantIdExpr}
    ORDER BY (${roleKeySeniorityCase('ir.key')}), ir.name
  )
`;

/**
 * `EXISTS` predicate: does `actorIdExpr` hold ANY of `roleKeys` in
 * `tenantIdExpr`? Genuinely multi-role-aware, unlike `buildRoleLateralJoin`
 * (which only ever resolves the single most-senior role) — an actor holding
 * more than one of the requested roles still matches once. `roleKeys` are
 * `iam_roles.key` values (see `roleKeyFromLegacyOrLiteral` to normalize a
 * caller-supplied legacy bucket string first) and are bound as a single
 * array parameter (`= ANY($N)`), matching this codebase's existing
 * many-valued-bind convention (e.g. `list-network-answers.ts`).
 */
export const buildHasAnyRoleWhereClause = (
  actorIdExpr: string,
  tenantIdExpr: string,
  roleKeys: readonly string[],
  bind: (value: unknown) => string,
): string => `
  EXISTS (
    SELECT 1 FROM iam_actor_roles iar
    JOIN iam_roles ir ON ir.id = iar.role_id
    WHERE iar.actor_type = 'user' AND iar.actor_id = ${actorIdExpr}
      AND ir.tenant_id = ${tenantIdExpr}
      AND ir.key = ANY(${bind(roleKeys)})
  )
`;
